/**
 * The CV Health launcher in the editor dock's leading slot (the twin of
 * `ActionDockHealthButton.tsx`): the design's Heart Agent (scene A4).
 *
 * One HTML button, a 48 px circle on the page colour with a hairline edge
 * and a soft shadow (global.css `.tabbio-health-dock`, which doubles as the
 * focus-return target of the CV Health dialog), holding the Health heart at
 * 32 px: a solid body in the toolbar's glyph colour and, drawn over it, the
 * signal line coloured by the score band (green from 80, amber from 40,
 * coral below, grey while there is no settled score). A requestAnimationFrame
 * loop sways the line along the 80 resampled signal points, one radian every
 * 800 ms on the clock, so a pause never jumps the phase. The exact score is
 * never drawn here; the accessibility label says it and the dialog shows it.
 *
 * The line holds the resting path under `prefers-reduced-motion`, inside an
 * `inert` ancestor (the dialog is open) and whenever the tab is hidden or
 * the button is off screen, where the loop stops altogether.
 */
import { useEffect, useRef, type RefObject } from 'react';
import {
  HEALTH_HEART_LINE_STROKE_WIDTH,
  HEALTH_HEART_PATH,
  HEALTH_HEART_SIGNAL_POINTS,
  HEALTH_HEART_VIEWBOX,
  HEALTH_HEART_WAVE,
  HEALTH_SIGNAL_PATH,
  getHeartSignalColor,
  heartSignalPath,
} from '@/components/cv-health/health-heart-path';
import { useTheme } from '../../../hooks/useTheme';
import { DOCK_ICON_COLOR, type ActionDockHealthConfig } from './constants';

const HEART_SIZE = 32;
const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/**
 * Drives the signal line's `d`. The loop runs only while the button is on
 * screen and the tab visible; each frame draws the wave, or the resting path
 * when motion is reduced or an `inert` ancestor covers the dock, and writes
 * the attribute only when it changes.
 */
function useHeartWave(lineRef: RefObject<SVGPathElement | null>) {
  useEffect(() => {
    const line = lineRef.current;
    if (!line || typeof window === 'undefined') return;
    const started = performance.now();
    const media = typeof window.matchMedia === 'function' ? window.matchMedia(REDUCED_MOTION) : null;
    let frame = 0;
    let inView = true;
    let pageVisible = !document.hidden;
    let last = '';

    const draw = (d: string) => {
      if (d === last) return;
      last = d;
      line.setAttribute('d', d);
    };
    const tick = (now: number) => {
      frame = 0;
      if (!line.isConnected) return;
      const still = media?.matches || line.closest('[inert]');
      draw(
        still
          ? HEALTH_SIGNAL_PATH
          : heartSignalPath(HEALTH_HEART_SIGNAL_POINTS, (now - started) / HEALTH_HEART_WAVE.msPerRad),
      );
      frame = requestAnimationFrame(tick);
    };
    const sync = () => {
      if (inView && pageVisible) {
        if (!frame) frame = requestAnimationFrame(tick);
        return;
      }
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      draw(HEALTH_SIGNAL_PATH);
    };
    const onVisibility = () => {
      pageVisible = !document.hidden;
      sync();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const observer =
      typeof IntersectionObserver === 'undefined'
        ? null
        : new IntersectionObserver(([entry]) => {
            inView = Boolean(entry?.isIntersecting);
            sync();
          });
    observer?.observe(line.closest('button') ?? line);
    sync();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener('visibilitychange', onVisibility);
      observer?.disconnect();
    };
  }, [lineRef]);
}

export function ActionDockHealthButton({
  state,
  score,
  onPress,
  a11yLabel,
  a11yHint,
}: ActionDockHealthConfig) {
  const { theme, isDark } = useTheme();
  const lineRef = useRef<SVGPathElement>(null);
  useHeartWave(lineRef);

  return (
    <button
      type="button"
      className="tabbio-health-dock"
      data-state={state}
      data-testid="cv-health-launcher"
      aria-label={a11yLabel}
      title={a11yHint}
      onClick={onPress}
      // The body takes the bar's glyph colour (ActionDock.web), so the heart
      // reads as one of the tools.
      style={{ color: isDark ? theme.text : DOCK_ICON_COLOR }}
    >
      <svg
        width={HEART_SIZE}
        height={HEART_SIZE}
        viewBox={HEALTH_HEART_VIEWBOX}
        aria-hidden="true"
        focusable="false"
      >
        <path d={HEALTH_HEART_PATH} fill="currentColor" />
        <path
          ref={lineRef}
          d={HEALTH_SIGNAL_PATH}
          fill="none"
          stroke={getHeartSignalColor(score)}
          strokeWidth={HEALTH_HEART_LINE_STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}

export default ActionDockHealthButton;
